<template>
  <multiselect
    ref="inner"
    :placeholder="selectValuePlaceholder"
    :style="selectorPlaceholder"
    v-bind="$attrs"
    class="woot-multiselect"
    v-on="mergedListeners"
    @hook:mounted="onMultiselectMounted"
  >
    <template v-for="(_, name) in filteredSlots" :slot="name">
      <slot :name="name" />
    </template>
    <template v-for="(_, name) in filteredScopedSlots" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
    <template #clear="slotData">
      <slot name="clear" v-bind="slotData">
        <span class="material-symbols-outlined search-icon">search</span>
      </slot>
    </template>
    <template #beforeList>
      <slot name="beforeList">
        <div class="top-list" />
      </slot>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { getFixedDialogStyle } from 'dashboard/helper/dialogPositionHelper';

const DEFAULT_SLOTS = ['clear', 'beforeList'];

export default {
  components: { Multiselect },
  inheritAttrs: false,
  props: {
    searchPlaceholder: {
      type: String,
      default: () => null,
    },
  },
  data() {
    return {
      dropdownOpened: false,
      selectValuePlaceholder:
        this.placeholder || this.$t('FILTER.SELECT_PLACEHOLDER'),
    };
  },
  computed: {
    filteredSlots() {
      return Object.fromEntries(
        Object.entries(this.$slots).filter(
          ([name]) => !DEFAULT_SLOTS.includes(name)
        )
      );
    },
    filteredScopedSlots() {
      return Object.fromEntries(
        Object.entries(this.$scopedSlots).filter(
          ([name]) => !DEFAULT_SLOTS.includes(name)
        )
      );
    },
    mergedListeners() {
      return {
        ...this.$listeners,
        open: (...args) => {
          this.onOpen(...args);
          this.$listeners.open?.(...args);
        },
        close: (...args) => {
          this.onClose(...args);
          this.$listeners.close?.(...args);
        },
      };
    },
    selectorPlaceholder() {
      if (this.values?.length || !this.dropdownOpened) return {};
      return {
        '--tags-before-content': `'${this.$attrs.placeholder}'`,
      };
    },
  },
  mounted() {
    this.$nextTick(this.calculateDropdownPosition);
  },
  methods: {
    onMultiselectMounted() {
      const el = this.$refs.inner.$el;
      const tags = el.querySelector('.multiselect__tags');
      const wrapper = el.querySelector('.multiselect__content-wrapper');
      const content = wrapper?.querySelector('.multiselect__content');
      const topList = content?.querySelector('.top-list');
      const input = tags?.querySelector('.multiselect__input');
      const searchIcon = el.querySelector('.search-icon');
      if (!this.$attrs.searchable) {
        searchIcon.remove();
        topList.remove();
      }
      if (!wrapper || !content || !topList || !input) return;

      const inputWrapper = document.createElement('div');
      inputWrapper.className = 'multiselect__input-wrapper';
      inputWrapper.appendChild(input);
      if (searchIcon) inputWrapper.appendChild(searchIcon);
      wrapper.insertBefore(inputWrapper, wrapper.firstChild);
      wrapper.insertBefore(topList, inputWrapper.nextSibling);
    },
    calculateDropdownPosition() {
      const el = this.$refs.inner.$el;
      const wrapper = el.querySelector('.multiselect__content-wrapper');
      if (!wrapper) return;

      const isAbove = el.classList.contains('multiselect--above');
      const rect = el.getBoundingClientRect();
      Object.assign(wrapper.style, {
        width: `${rect.width}px`,
        bottom: 'auto',
        ...getFixedDialogStyle(el, {
          width: rect.width,
          height: wrapper.offsetHeight,
          gap: 0,
          align: 'left',
          preferredPlacement: isAbove ? 'top' : 'bottom',
        }),
      });
    },
    getScrollTargets() {
      let node = this.$refs.inner.$el.parentElement;
      while (node) {
        const { overflow, overflowY, overflowX } = getComputedStyle(node);
        if (/auto|scroll/.test(overflow + overflowY + overflowX)) {
          return [node, window];
        }
        node = node.parentElement;
      }
      return [window];
    },
    onOpen() {
      this.dropdownOpened = true;
      this.selectValuePlaceholder =
        this.searchPlaceholder ??
        this.$attrs.placeholder ??
        this.$t('MULTISELECT.SEARCH_PLACEHOLDER');
      this.$refs.inner.$el.style.zIndex = 65;
      this.$nextTick(this.calculateDropdownPosition);
      this.getScrollTargets().forEach(el =>
        el.addEventListener('scroll', this.calculateDropdownPosition)
      );
    },
    onClose() {
      this.dropdownOpened = false;
      this.selectValuePlaceholder =
        this.searchPlaceholder ??
        this.$attrs.placeholder ??
        this.$t('MULTISELECT.SEARCH_PLACEHOLDER');
      this.$refs.inner.$el.style.zIndex = 0;
      this.getScrollTargets().forEach(el =>
        el.removeEventListener('scroll', this.calculateDropdownPosition)
      );
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  &.woot-multiselect {
    &.multiselect {
      .multiselect__content {
        .multiselect__option.multiselect__option--selected,
        .multiselect__option.multiselect__option--highlight:hover,
        .multiselect__option.multiselect__option--selected.multiselect__option--highlight:hover {
          @apply bg-[#F0FDF4];

          span {
            @apply text-[#164B35];
          }

          &::after {
            @apply bg-[#F0FDF4];
          }
        }
      }

      .multiselect__option.multiselect__option--selected {
        &:after {
          background-image: url(/images/checked.svg);
          width: 11px;
          height: 8px;
          background-origin: content-box;
          background-position-x: center;
          background-repeat: no-repeat;
          background-size: 11px 8px;
          top: 50%;
          transform: translateY(-50%);
          right: 12px;
          padding: 0;
        }
      }

      .multiselect__tag-icon {
        &:hover::after {
          @apply text-green-500;
        }
        &::after {
          @apply text-slate-500 leading-[26px];
        }
      }

      .multiselect__select {
        width: 9px;
        margin-right: 12px;
        min-height: 44px;
        &:before {
          border: none;
          background-image: url(/images/arrow-down.svg);
          background-origin: content-box;
          background-position-x: center;
          background-repeat: no-repeat;
          background-size: 9px 6px;
          width: 9px;
          height: 6px;
          display: block;
          position: absolute;
          top: 2px;
          left: 0;
          right: 0;
          bottom: 0;
          margin: auto 0;
        }
      }

      &.multiselect--active {
        .multiselect__select {
          transform: scaleY(-1);
        }

        &:not(.multiselect--above) .multiselect__current,
        &:not(.multiselect--above) .multiselect__input,
        &:not(.multiselect--above) .multiselect__tags {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
        }

        &.multiselect--above .multiselect__current,
        &.multiselect--above .multiselect__input,
        &.multiselect--above .multiselect__tags {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }

        &.multiselect--above .multiselect__content-wrapper,
        .multiselect__content-wrapper {
          border-radius: 8px;
          margin-top: 4px;
          border-color: #e5e7eb;

          overflow: hidden;
          display: flex;
          flex-direction: column;

          .multiselect__content {
            overflow-y: auto;
            flex: 1;

            &::-webkit-scrollbar {
              width: 3px;
            }

            &::-webkit-scrollbar-track {
              background: transparent;
            }

            &::-webkit-scrollbar-thumb {
              @apply bg-gray-300 rounded-full;
            }

            &::-webkit-scrollbar-thumb:hover {
              @apply bg-gray-400;
            }
          }

          .multiselect__input-wrapper {
            @apply relative w-full p-2;
            border-bottom: 1px solid #e5e7eb;

            .multiselect__input {
              border: 1px solid #d1d5db;
              height: 40px !important;
              @apply rounded-[8px] static w-full;

              &::placeholder {
                color: #9ca3af;
              }
            }
          }
        }

        .multiselect__tags {
          @apply border-[#9CA3AF];
        }

        .multiselect__input {
          height: 42px !important;
          line-height: 42px;
          font-size: 16px;
          &::placeholder {
            font-size: 16px;
          }
        }

        &.multiselect--above {
          .multiselect__content-wrapper {
            overflow: hidden;
          }
        }

        &:not(.multiselect--above) .multiselect__content-wrapper {
          box-shadow: 0 1px 2px #1118271a;
        }
      }
      .search-icon {
        @apply text-gray-500 absolute top-[16px] z-[50];
      }

      .multiselect__tags {
        @apply min-h-[44px] border-gray-300 hover:border-gray-400 rounded-[8px] pl-3;
        @apply max-h-[250px] overflow-y-auto flex flex-col justify-center;

        &::before {
          content: var(--tags-before-content, '');
          position: absolute;
          font-size: 16px;
          line-height: 20px;
          top: 12px;
          color: #1f2937;
        }

        .multiselect__input {
          height: 42px !important;
          line-height: 42px;
          font-size: 16px;
          &::placeholder {
            font-size: 16px;
          }
        }
        .multiselect__tag {
          @apply my-[1px] mr-1;
        }

        .multiselect__placeholder {
          @apply text-base m-0 p-0 leading-[42px] text-gray-800;
        }

        .multiselect__tags-wrap {
          @apply m-0 flex flex-row flex-wrap gap-[2px] items-center py-[2px];
        }
      }

      .multiselect__single {
        @apply py-2.5 px-0 text-base leading-[22px];
      }

      .multiselect__element {
        .multiselect__option {
          @apply py-0 flex flex-col justify-center items-start h-[44px] text-base;
        }
        .multiselect__option--disabled.multiselect__option--group {
          @apply text-sm font-semibold;
          color: #9CA3AF !important;
          background-color: white !important;
        }
      }

      .multiselect__content-wrapper {
        border-end-start-radius: 8px;
        border-end-end-radius: 8px;

        &::-webkit-scrollbar {
          width: 3px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          @apply bg-gray-300 rounded-full;
        }

        &::-webkit-scrollbar-thumb:hover {
          @apply bg-gray-400;
        }
      }
    }
  }
}
</style>
<style lang="scss">
[dir='ltr'] {
  .woot-multiselect {
    &.multiselect {
      .search-icon {
        @apply left-[18px];
      }

      .multiselect__tags::before {
        left: 13px;
      }

      .multiselect__input-wrapper {
        .multiselect__input {
          @apply pl-[44px];
        }
      }
    }
  }
}

[dir='rtl'] {
  .woot-multiselect {
    &.multiselect {
      .multiselect__option.multiselect__option--selected {
        &::after {
          right: unset;
          left: 12px;
        }
      }

      .search-icon {
        @apply right-[18px];
      }

      .multiselect__select {
        margin-right: unset;
        margin-left: 13px;

        &::before {
          right: unset !important;
        }
      }

      .multiselect__tags {
        padding-right: 12px;
        padding-left: 40px;

        .multiselect__tag {
          @apply mr-0 ml-1 pr-[10px] pl-[24px];

          .multiselect__tag-icon {
            margin-right: 7px;
            margin-left: 0;
            left: 0;
            right: unset;
          }
        }
      }

      .multiselect__tags::before {
        right: 13px;
      }

      .multiselect__input-wrapper {
        .multiselect__input {
          @apply pr-[44px];
        }
      }
    }
  }
}
</style>
